import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Database,
  X,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

interface NewUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewUserModal: React.FC<NewUserModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { setIsNewUser } = useAuth();

  if (!isOpen) return null;

  const handleGetStarted = () => {
    setIsNewUser(false);
    navigate('/integrations');
    onClose();
  };

  const handleSkip = () => {
    setIsNewUser(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-lg mx-4">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Niwt!</h2>
            <p className="text-gray-600">Get started by setting up your email ingestion</p>
          </div>
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-8">
          <div className="bg-purple-50 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Email Ingestion</h3>
                <p className="text-sm text-gray-600">Import your Outlook emails for AI analysis</p>
              </div>
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Secure OAuth authentication with Microsoft</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>AI-powered email vectorization and search</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Private and secure data storage</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Next steps:</strong> You'll authenticate with Microsoft and select which emails to import. 
              Your data is processed securely and stored in your private AWS environment.
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleGetStarted}
            className="flex-1 bg-purple-700 text-white px-6 py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            Get Started
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleSkip}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewUserModal;